import { useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { PACTE_MIGRATOIRE_FAQ } from "../data/pacteMigratoireFaq";
import { ACHETER_BIEN_FAQ } from "../data/acheterBienFaq";

// Single source of truth for per-route SEO. On every route change we UPDATE the
// existing head tags in place (upsert) rather than appending new ones, so each
// page renders exactly one of every tag type. The defaults baked into
// public/index.html serve as fallback for the homepage and as no-JS fallback
// for crawlers; this component overrides them route by route at runtime.
//
// Two modes of usage:
//   1. Global (default): a single <SeoHead /> rendered at the layout level
//      (no props). It reads PAGE_SEO[pathname] and applies tags.
//   2. Page-level publisher: <SeoHead title="..." description="..." canonical="..."
//      breadcrumb={...} /> rendered from inside a page component. It publishes
//      an override to the global instance via a module-level subscription; the
//      global instance picks up the override and merges it on top of PAGE_SEO
//      for the current pathname. The override unmounts when the page does, so
//      no other page's SEO is affected.

const BASE_URL = "https://hejpoland.com";
const SITE_NAME = "HejPoland";
const NOINDEX_PREFIXES = ["/en/", "/pl/"];
const NOINDEX_PATHS = ["/fr/education"];

// Default social card shared across all pages. Per-page overrides could be
// introduced later via PAGE_SEO.image / image_alt etc.
const DEFAULT_OG_IMAGE = "https://hejpoland.com/og-image.png";
const DEFAULT_OG_IMAGE_WIDTH = "1200";
const DEFAULT_OG_IMAGE_HEIGHT = "630";
const DEFAULT_OG_IMAGE_ALT = "HejPoland, guide francophone pour s'installer en Pologne";

// Brand logo used as publisher.logo in BlogPosting JSON-LD. Raster PNG with
// a transparent background, generated from the Navbar's Hej!Poland wordmark.
const DEFAULT_PUBLISHER_LOGO = "https://hejpoland.com/logo-hejpoland.png";
const DEFAULT_PUBLISHER_LOGO_WIDTH = 600;
const DEFAULT_PUBLISHER_LOGO_HEIGHT = 103;

// Per-route SEO overrides. Routes not listed inherit the static defaults from
// index.html (title and description). og:url is always derived from the path,
// so it cannot drift away from the canonical. The optional `breadcrumb` field
// is the displayed name of the current page in a BreadcrumbList JSON-LD; if
// present, a BreadcrumbList script is upserted in the head.
const PAGE_SEO = {
  "/fr/":                {
    title: "S'installer en Pologne : le guide francophone | HejPoland",
    description: "Le guide francophone pour s'installer en Pologne : logement, coût de la vie, démarches et entrepreneuriat. Informations vérifiées et sourcées, ville par ville.",
  },
  "/fr/logement":        { title: "Se loger en Pologne : loyers et conseils pratiques | HejPoland", breadcrumb: "Logement" },
  "/fr/acheter-bien-immobilier-pologne": {
    title: "Acheter un appartement ou une maison en Pologne : règles par nationalité",
    description:
      "Acheter un bien immobilier en Pologne : libre pour les ressortissants UE et suisses, soumis à autorisation pour les pays tiers, sauf pour un appartement.",
    type: "article",
    breadcrumb: [
      { name: "Logement", path: "/fr/logement" },
      { name: "Acheter un bien immobilier", path: "/fr/acheter-bien-immobilier-pologne" },
    ],
    article: {
      headline: "Puis-je acheter un appartement ou une maison en Pologne ?",
      datePublished: "2026-06-30",
      dateModified: "2026-06-30",
      inLanguage: "fr",
    },
    faqPage: ACHETER_BIEN_FAQ,
  },
  "/fr/cout-de-la-vie":  { title: "Coût de la vie en Pologne : comparatif des 7 villes | HejPoland", breadcrumb: "Coût de la vie" },
  "/fr/demarches":       { title: "Démarches pour s'installer en Pologne | HejPoland", breadcrumb: "Démarches" },
  "/fr/retraite-pologne": {
    title: "Prendre sa retraite en Pologne : fiscalité, santé et séjour | HejPoland",
    description:
      "Guide pour prendre sa retraite en Pologne : fiscalité de votre pension, santé via le S1 et droit au séjour, quand on vient de France, de Belgique ou de Suisse.",
    type: "article",
    breadcrumb: [
      { name: "Démarches", path: "/fr/demarches" },
      { name: "Prendre sa retraite en Pologne", path: "/fr/retraite-pologne" },
    ],
    article: {
      headline: "Prendre sa retraite en Pologne",
      datePublished: "2026-06-30",
      dateModified: "2026-06-30",
      inLanguage: "fr",
    },
  },
  "/fr/sante": {
    title: "Se soigner en Pologne : système de santé, NFZ et privé | HejPoland",
    description:
      "Guide pratique pour se soigner en Pologne : le système public NFZ, la couverture selon votre statut, l'accès aux soins et le recours au privé (LuxMed, Medicover).",
    type: "article",
    breadcrumb: "Santé",
    article: {
      headline: "Se soigner en Pologne",
      datePublished: "2026-07-01",
      dateModified: "2026-07-01",
      inLanguage: "fr",
    },
  },
  "/fr/travailler-pologne": {
    title: "Travailler en Pologne : salaires, contrats et marché de l'emploi (2026)",
    description:
      "Salaire minimum et salaire moyen 2026, types de contrats, brut-net, secteurs qui recrutent des francophones. Le guide complet pour travailler en Pologne, chiffres officiels à l'appui.",
    type: "article",
    breadcrumb: "Travailler en Pologne",
    article: {
      headline: "Travailler en Pologne",
      datePublished: "2026-07-02",
      dateModified: "2026-07-02",
      inLanguage: "fr",
    },
  },
  "/fr/demarches-pays-tiers": {
    title: "S'installer en Pologne quand on vient d'un pays tiers | HejPoland",
    description:
      "Le parcours complet pour s'installer en Pologne quand on vient d'un pays tiers, hors UE : visa, permis de travail, carte de séjour et étapes après l'arrivée.",
    breadcrumb: [
      { name: "Démarches", path: "/fr/demarches" },
      { name: "Pays tiers", path: "/fr/demarches-pays-tiers" },
    ],
  },
  "/fr/connaissances": {
    title: "Connaissances sur la Pologne : quiz et culture | HejPoland",
    description:
      "Testez vos connaissances sur la Pologne avec nos quiz thématiques sourcés. Premier thème : le président Karol Nawrocki en 37 questions.",
    breadcrumb: "Connaissances",
  },
  "/fr/entrepreneuriat": {
    title: "Créer son entreprise en Pologne : JDG, Sp. z o.o. et fiscalité | HejPoland",
    description:
      "Entreprendre en Pologne : entreprise individuelle (JDG) ou société (Sp. z o.o.), régimes fiscaux, cotisations ZUS, TVA et convention fiscale franco-polonaise.",
    type: "article",
    breadcrumb: "Entrepreneuriat",
  },
  "/fr/blog":            { breadcrumb: "Blog" },
  "/fr/blog/pacte-migratoire-pologne": {
    title: "Pacte migratoire de l'UE : pourquoi la Pologne refuse la relocalisation | HejPoland",
    description:
      "Le pacte migratoire européen s'applique depuis le 12 juin 2026. Pourquoi la Pologne refuse la relocalisation, son exemption et la contestation à la frontière biélorusse.",
    type: "article",
    breadcrumb: [
      { name: "Blog", path: "/fr/blog" },
      { name: "Pacte migratoire de l'UE", path: "/fr/blog/pacte-migratoire-pologne" },
    ],
    blogPosting: {
      headline: "Pacte migratoire de l'UE : pourquoi la Pologne refuse la relocalisation",
      datePublished: "2026-06-26",
      dateModified: "2026-06-26",
      inLanguage: "fr-FR",
    },
    faqPage: PACTE_MIGRATOIRE_FAQ,
  },
  "/fr/blog/travailler-pologne-reforme-2025": {
    title: "Travailler en Pologne après la réforme de 2025 | HejPoland",
    description:
      "Depuis le 1er juin 2025, la Pologne a réformé l'emploi des étrangers. Ce qui change pour les ressortissants de pays tiers, pourquoi et les premières conséquences.",
    type: "article",
    breadcrumb: [
      { name: "Blog", path: "/fr/blog" },
      { name: "Travailler en Pologne après la réforme de 2025", path: "/fr/blog/travailler-pologne-reforme-2025" },
    ],
    blogPosting: {
      headline: "Travailler en Pologne après la réforme de 2025 : ce qui change pour les ressortissants de pays tiers",
      datePublished: "2026-06-15",
      dateModified: "2026-06-15",
      inLanguage: "fr-FR",
    },
  },
  "/fr/blog/sante-pologne-scandale-szpital-poludniowy": {
    title: "Santé en Pologne : le scandale du Szpital Południowy expliqué | HejPoland",
    description:
      "Système de santé polonais : ce que le scandale du Szpital Południowy révèle sur l'accès aux soins, le rôle du NFZ et ce qui change pour un expatrié.",
    type: "article",
    breadcrumb: [
      { name: "Blog", path: "/fr/blog" },
      { name: "Scandale du Szpital Południowy", path: "/fr/blog/sante-pologne-scandale-szpital-poludniowy" },
    ],
    blogPosting: {
      headline: "Santé en Pologne : le scandale du Szpital Południowy expliqué",
      datePublished: "2026-07-08",
      dateModified: "2026-07-08",
      inLanguage: "fr-FR",
    },
  },
  "/fr/logement/immobilier-wroclaw": {
    title: "Immobilier à Wrocław : quartiers, prix achat et location 2026 | HejPoland",
    description:
      "Quartiers les plus prisés de Wrocław, prix au m² à l'achat et à la location, rendement locatif et règles d'achat pour Français, Belges et Suisses.",
    type: "article",
    breadcrumb: [
      { name: "Logement", path: "/fr/logement" },
      { name: "Immobilier à Wrocław", path: "/fr/logement/immobilier-wroclaw" },
    ],
    article: {
      headline: "Immobilier à Wrocław : quartiers prisés, prix d'achat et de location",
      datePublished: "2026-07-17",
      dateModified: "2026-07-17",
      inLanguage: "fr",
    },
  },
};

// City detail routes own their <title>, <meta og:title> and <meta twitter:title>
// themselves (the title depends on async-loaded city data). SeoHead must not
// overwrite those tags on this route, otherwise the city title would flash back
// to a stale default on re-renders or city-to-city navigation.
const CITY_ROUTE_RE = /^\/(fr|en|pl)\/villes\//;

// Upsert helpers ---------------------------------------------------------------

function upsertMeta(attrName, attrValue, content) {
  const selector = `meta[${attrName}="${attrValue}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  const selector = `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeAll(selector) {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
}

// One-time cleanup of legacy injection patterns that earlier iterations of the
// app shipped (per-page useEffect that appended duplicate tags).
function purgeLegacyTags() {
  removeAll("[data-entrepreneuriat-seo]");
  removeAll("[data-seo-head]");
  // Also de-duplicate any stale duplicate meta description / og:* / twitter:*
  // tags that might have accumulated from previous renders. We keep only the
  // first occurrence per selector; the upsert pass below will rewrite its
  // content immediately after.
  const dedupeSelectors = [
    'meta[name="description"]',
    'meta[name="robots"]',
    'meta[property="og:type"]',
    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[property="og:url"]',
    'meta[property="og:site_name"]',
    'meta[name="twitter:card"]',
    'meta[name="twitter:title"]',
    'meta[name="twitter:description"]',
    'link[rel="canonical"]',
  ];
  for (const sel of dedupeSelectors) {
    const nodes = document.head.querySelectorAll(sel);
    for (let i = 1; i < nodes.length; i++) nodes[i].remove();
  }
}

// -----------------------------------------------------------------------------

// Module-level override store (publisher / subscriber). A page-level
// <SeoHead title=... /> publishes here; the global instance reads from it via
// useSyncExternalStore so React knows when to re-apply the effect.
let _override = null;
const _listeners = new Set();
function subscribeOverride(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}
function getOverrideSnapshot() {
  return _override;
}
function publishOverride(value) {
  _override = value;
  _listeners.forEach((fn) => fn());
}

// -----------------------------------------------------------------------------

export default function SeoHead({
  title: titleProp,
  description: descriptionProp,
  canonical: canonicalProp,
  breadcrumb: breadcrumbProp,
} = {}) {
  const { pathname } = useLocation();
  const hasProps = !!(
    titleProp || descriptionProp || canonicalProp || breadcrumbProp
  );

  // Publisher mode: register the override and clean it up on unmount /
  // pathname change. Stringify the breadcrumb to keep deps stable across
  // re-renders that pass a fresh array literal.
  const breadcrumbKey = breadcrumbProp ? JSON.stringify(breadcrumbProp) : "";
  useEffect(() => {
    if (!hasProps) return undefined;
    publishOverride({
      pathname,
      title: titleProp,
      description: descriptionProp,
      canonical: canonicalProp,
      breadcrumb: breadcrumbProp,
    });
    return () => {
      // Only clear if we are the active publisher (avoid clobbering a sibling
      // override that may have already been published).
      if (_override && _override.pathname === pathname) publishOverride(null);
    };
  }, [hasProps, pathname, titleProp, descriptionProp, canonicalProp, breadcrumbKey, breadcrumbProp]);

  // Applier mode (global instance, no props): read the override store and
  // apply head tags. The publisher mode also subscribes harmlessly; its
  // useEffect below returns early.
  const override = useSyncExternalStore(subscribeOverride, getOverrideSnapshot, getOverrideSnapshot);

  useEffect(() => {
    if (hasProps) return; // page-level publisher never applies tags itself
    purgeLegacyTags();

    // Normalize the homepage path so /fr and /fr/ share the same canonical.
    const normalizedPath =
      pathname === "/fr" ? "/fr/" :
      pathname === "/en" ? "/en/" :
      pathname === "/pl" ? "/pl/" : pathname;

    const baseSeo = PAGE_SEO[normalizedPath] || {};
    const activeOverride =
      override && override.pathname === normalizedPath ? override : null;

    // Resolved SEO values: explicit override props win over PAGE_SEO, which
    // wins over the static defaults shipped by index.html.
    const seo = activeOverride
      ? {
          title: activeOverride.title || baseSeo.title,
          description: activeOverride.description || baseSeo.description,
          breadcrumb: activeOverride.breadcrumb || baseSeo.breadcrumb,
          type: baseSeo.type,
          blogPosting: baseSeo.blogPosting,
          article: baseSeo.article,
          faqPage: baseSeo.faqPage,
        }
      : baseSeo;
    const isCityRoute = CITY_ROUTE_RE.test(normalizedPath);

    // Resolve title and description, falling back to whatever index.html ships.
    const defaultTitle = document.head.querySelector("title")?.textContent || "";
    const defaultDescription =
      document.head.querySelector('meta[name="description"]')?.getAttribute("content") || "";

    const title = seo.title || defaultTitle;
    const description = seo.description || defaultDescription;
    const type = seo.type || "website";
    const url =
      (activeOverride && activeOverride.canonical) ||
      `${BASE_URL}${normalizedPath}`;

    // Set <title> and <html lang>. City routes manage their own title
    // (depends on async-loaded data), so we leave it alone there.
    if (!isCityRoute && document.title !== title) document.title = title;
    const lang = normalizedPath.startsWith("/en")
      ? "en"
      : normalizedPath.startsWith("/pl")
      ? "pl"
      : "fr";
    if (document.documentElement.lang !== lang) document.documentElement.lang = lang;

    // Robots / canonical: noindex on /en/*, /pl/*, and exact paths listed in
    // NOINDEX_PATHS (e.g. /fr/education while the section is a placeholder).
    // Canonical only on other /fr/* pages.
    const isNoindex =
      NOINDEX_PREFIXES.some(
        (p) => normalizedPath === p.slice(0, -1) || normalizedPath.startsWith(p)
      ) || NOINDEX_PATHS.includes(normalizedPath);
    if (isNoindex) {
      upsertMeta("name", "robots", "noindex, follow");
      removeAll('link[rel="canonical"]');
    } else {
      removeAll('meta[name="robots"]');
      upsertLink("canonical", url);
    }

    // Description, Open Graph, Twitter Cards: exactly one of each.
    // On city routes we skip title-bearing tags so CityDetail can own them
    // without SeoHead clobbering its async-resolved values on re-renders.
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:type", type);
    if (!isCityRoute) upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta(
      "property",
      "og:locale",
      lang === "en" ? "en_US" : lang === "pl" ? "pl_PL" : "fr_FR"
    );
    upsertMeta("property", "og:image", DEFAULT_OG_IMAGE);
    upsertMeta("property", "og:image:width", DEFAULT_OG_IMAGE_WIDTH);
    upsertMeta("property", "og:image:height", DEFAULT_OG_IMAGE_HEIGHT);
    upsertMeta("property", "og:image:alt", DEFAULT_OG_IMAGE_ALT);
    upsertMeta("name", "twitter:card", "summary_large_image");
    if (!isCityRoute) upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", DEFAULT_OG_IMAGE);

    // BreadcrumbList JSON-LD: upsert one if the route declares a breadcrumb,
    // remove any leftover otherwise. We mark the tag with data-seo-breadcrumb
    // so we never touch the global WebSite JSON-LD shipped by index.html.
    // `seo.breadcrumb` accepts either:
    //   - a string: implies a 2-level breadcrumb Accueil > <string>
    //   - an array of { name, path }: explicit multi-level breadcrumb
    //     (Accueil is prepended automatically).
    const BREADCRUMB_SELECTOR =
      'script[type="application/ld+json"][data-seo-breadcrumb]';
    if (seo.breadcrumb && !isNoindex) {
      let items;
      if (Array.isArray(seo.breadcrumb)) {
        items = [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${BASE_URL}/fr/` },
          ...seo.breadcrumb.map((b, i) => ({
            "@type": "ListItem",
            position: i + 2,
            name: b.name,
            item: `${BASE_URL}${b.path}`,
          })),
        ];
      } else {
        items = [
          { "@type": "ListItem", position: 1, name: "Accueil",        item: `${BASE_URL}/fr/` },
          { "@type": "ListItem", position: 2, name: seo.breadcrumb,   item: url },
        ];
      }
      const payload = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
      };
      let el = document.head.querySelector(BREADCRUMB_SELECTOR);
      if (!el) {
        el = document.createElement("script");
        el.setAttribute("type", "application/ld+json");
        el.setAttribute("data-seo-breadcrumb", "");
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(payload);
    } else {
      removeAll(BREADCRUMB_SELECTOR);
    }

    // BlogPosting JSON-LD: upsert when the route declares a `blogPosting`
    // payload, remove any leftover otherwise. Tagged with its own data
    // attribute so it never collides with BreadcrumbList or the global
    // WebSite JSON-LD shipped by index.html. `image`, `author`, `publisher`
    // and `mainEntityOfPage` are derived from site constants and the current
    // URL, so callers only need to supply article-specific fields
    // (headline, dates, inLanguage).
    const BLOG_POSTING_SELECTOR =
      'script[type="application/ld+json"][data-seo-blogposting]';
    if (seo.blogPosting && !isNoindex) {
      const bp = seo.blogPosting;
      const publisherLogo = {
        "@type": "ImageObject",
        url: DEFAULT_PUBLISHER_LOGO,
        width: DEFAULT_PUBLISHER_LOGO_WIDTH,
        height: DEFAULT_PUBLISHER_LOGO_HEIGHT,
      };
      const authorRef = { "@type": "Organization", name: SITE_NAME, url: `${BASE_URL}/fr/` };
      const publisherRef = { ...authorRef, logo: publisherLogo };
      const bpPayload = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: bp.headline,
        description,
        image: [DEFAULT_OG_IMAGE],
        datePublished: bp.datePublished,
        dateModified: bp.dateModified || bp.datePublished,
        inLanguage: bp.inLanguage || "fr-FR",
        author: authorRef,
        publisher: publisherRef,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
      };
      let bpEl = document.head.querySelector(BLOG_POSTING_SELECTOR);
      if (!bpEl) {
        bpEl = document.createElement("script");
        bpEl.setAttribute("type", "application/ld+json");
        bpEl.setAttribute("data-seo-blogposting", "");
        document.head.appendChild(bpEl);
      }
      bpEl.textContent = JSON.stringify(bpPayload);
    } else {
      removeAll(BLOG_POSTING_SELECTOR);
    }

    // Article JSON-LD: mirrors BlogPosting structure but uses @type=Article.
    // Reserved for non-blog long-form content pages (e.g. Logement guides)
    // that should not be reported as blog posts to search engines. Tagged
    // with its own data attribute so it never collides with BlogPosting.
    const ARTICLE_SELECTOR =
      'script[type="application/ld+json"][data-seo-article]';
    if (seo.article && !isNoindex) {
      const a = seo.article;
      const publisherLogo = {
        "@type": "ImageObject",
        url: DEFAULT_PUBLISHER_LOGO,
        width: DEFAULT_PUBLISHER_LOGO_WIDTH,
        height: DEFAULT_PUBLISHER_LOGO_HEIGHT,
      };
      const authorRef = { "@type": "Organization", name: SITE_NAME, url: `${BASE_URL}/fr/` };
      const publisherRef = { ...authorRef, logo: publisherLogo };
      const aPayload = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: a.headline,
        description,
        image: [DEFAULT_OG_IMAGE],
        datePublished: a.datePublished,
        dateModified: a.dateModified || a.datePublished,
        inLanguage: a.inLanguage || "fr-FR",
        author: authorRef,
        publisher: publisherRef,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
      };
      let aEl = document.head.querySelector(ARTICLE_SELECTOR);
      if (!aEl) {
        aEl = document.createElement("script");
        aEl.setAttribute("type", "application/ld+json");
        aEl.setAttribute("data-seo-article", "");
        document.head.appendChild(aEl);
      }
      aEl.textContent = JSON.stringify(aPayload);
    } else {
      removeAll(ARTICLE_SELECTOR);
    }

    // FAQPage JSON-LD: upsert when the route declares a `faqPage` array of
    // { q, a } pairs, remove any leftover otherwise. Tagged with its own data
    // attribute so it never collides with BreadcrumbList or BlogPosting.
    const FAQ_PAGE_SELECTOR =
      'script[type="application/ld+json"][data-seo-faqpage]';
    if (Array.isArray(seo.faqPage) && seo.faqPage.length > 0 && !isNoindex) {
      const faqPayload = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: seo.faqPage.map((pair) => ({
          "@type": "Question",
          name: pair.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: pair.a,
          },
        })),
      };
      let faqEl = document.head.querySelector(FAQ_PAGE_SELECTOR);
      if (!faqEl) {
        faqEl = document.createElement("script");
        faqEl.setAttribute("type", "application/ld+json");
        faqEl.setAttribute("data-seo-faqpage", "");
        document.head.appendChild(faqEl);
      }
      faqEl.textContent = JSON.stringify(faqPayload);
    } else {
      removeAll(FAQ_PAGE_SELECTOR);
    }
  }, [pathname, hasProps, override]);

  return null;
}
